import { api } from "@/lib/api";
import { idb } from "@/lib/offlineDb";

export const TTS = { es: "es-ES", fr: "fr-FR", de: "de-DE", ja: "ja-JP", it: "it-IT" };

function browserSpeak(text, lang, rate) {
  const synth = window.speechSynthesis;
  if (!synth) return;
  synth.cancel();
  const code = TTS[lang] || lang;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = code;
  u.rate = rate;
  const voice = synth.getVoices().find((v) => v.lang?.replace("_", "-").toLowerCase().startsWith(code.slice(0, 2)));
  if (voice) u.voice = voice;
  synth.speak(u);
}

const audioCache = new Map();
let current = null;

async function audioUrl(text, slow) {
  const key = `${slow}|${text}`;
  if (audioCache.has(key)) return audioCache.get(key);
  let blob = await idb.get("audio", key).catch(() => null);
  if (!blob) {
    blob = (await api.post("/tts", { text, slow }, { responseType: "blob" })).data;
    idb.set("audio", key, blob).catch(() => {});
  }
  const url = URL.createObjectURL(blob);
  audioCache.set(key, url);
  return url;
}

export async function prefetchAudio(texts) {
  const list = [...new Set(texts)];
  for (let i = 0; i < list.length; i += 4) {
    await Promise.all(list.slice(i, i + 4).map((t) => audioUrl(t, false).catch(() => null)));
  }
}

export async function speak(text, lang, rate = 1) {
  if (!text) return;
  try {
    const url = await audioUrl(text, rate < 1);
    current?.pause();
    current = new Audio(url);
    await current.play();
  } catch {
    browserSpeak(text, lang, rate);
  }
}

export function norm(s) {
  return String(s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f\u3099\u309a]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");
}

const romajiKey = (s) =>
  s.replace(/ /g, "").replace(/ou/g, "o").replace(/oo/g, "o").replace(/uu/g, "u").replace(/aa/g, "a").replace(/ee/g, "e").replace(/ii/g, "i");
// eslint-disable-next-line no-control-regex
const isAscii = (s) => /^[\x00-\x7F]*$/.test(s);

export function answerMatches(given, accepted) {
  const g = norm(given);
  if (!g) return false;
  return accepted.filter(Boolean).some((a) => {
    const n = norm(a);
    if (n === g || n.replace(/ /g, "") === g.replace(/ /g, "")) return true;
    return isAscii(g) && isAscii(n) && romajiKey(g) === romajiKey(n);
  });
}

export const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
